import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2, Globe } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Input,
  Textarea,
  Checkbox,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useWaapEnabledSites,
  useCreateWebAccessControlRule,
  useWebAccessControlRule,
  useUpdateWebAccessControlRule,
  emptyWebAccessControlRule,
  PBR_PATH_CONDITIONS,
  PBR_WEB_ACCESS_KEYS,
  PBR_DEVICE_TYPES,
  PBR_AUTO_LIST_KEYS,
  PBR_PROBABILITIES,
  type WebAccessControlRuleInput,
} from '@/api/waap';
import { EmptyState } from '@vnetwork/ui/data';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './WebAccessControlRuleForm.module.css';

const NAME_MAX = 26;
const DESC_MAX = 200;
const PATH_MAX = 50;

/**
 * Add a Programmable Mitigation → Web Access Control rule. Account-level: the
 * rule is created once and applied to one or more websites ("Apply to"), via the
 * PUBLIC Open-API surface. Translated to console conventions (sectioned form, not
 * the vendor's flat layout). The public PBR DTO carries no Filters or Action Set,
 * so those sections are omitted (see src/api/waap/pbr.ts).
 */
export function WebAccessControlRuleForm() {
  const navigate = useNavigate();
  const { serviceId, ruleId } = useParams<{ serviceId: string; ruleId: string }>();
  const isEdit = !!ruleId;
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;
  const listHref = `${base}/programmable-mitigation`;

  const { selected } = useWaapWebsite();
  const domainId = selected?.domainId;
  const { data: sites = [] } = useWaapEnabledSites(domainId);

  const [form, setForm] = useState<WebAccessControlRuleInput>(emptyWebAccessControlRule);

  // Edit mode: seed the form from the rule's detail once it loads.
  const { data: detail } = useWebAccessControlRule(domainId, ruleId);
  useEffect(() => {
    if (!detail) return;
    setForm({
      name: detail.name,
      description: detail.description,
      path_operator: detail.path_operator,
      path_pattern: detail.path_pattern,
      key_type: detail.key_type,
      key_value: detail.key_value ?? [],
      key_regex: detail.key_regex ?? false,
      dev_type: detail.dev_type ?? [],
      triggering_probability: detail.triggering_probability,
      auto_list_key: detail.auto_list_key,
      auto_list_duration: detail.auto_list_duration,
      websites: detail.websites ?? [],
    });
  }, [detail]);

  const set = <K extends keyof WebAccessControlRuleInput>(
    key: K,
    value: WebAccessControlRuleInput[K],
  ) => setForm((f) => ({ ...f, [key]: value }));

  // Key values — tag input ("Input your data and press Enter to submit").
  const [keyInput, setKeyInput] = useState('');
  const addKeyValue = () => {
    const v = keyInput.trim();
    if (v && !form.key_value.includes(v)) set('key_value', [...form.key_value, v]);
    setKeyInput('');
  };
  const removeKeyValue = (v: string) =>
    set('key_value', form.key_value.filter((x) => x !== v));

  // Device type toggles.
  const devSet = useMemo(() => new Set(form.dev_type), [form.dev_type]);
  const toggleDev = (v: string) =>
    set('dev_type', devSet.has(v) ? form.dev_type.filter((x) => x !== v) : [...form.dev_type, v]);

  // Apply-to toggles.
  const siteSet = useMemo(() => new Set(form.websites), [form.websites]);
  const toggleSite = (id: number) =>
    set('websites', siteSet.has(id) ? form.websites.filter((x) => x !== id) : [...form.websites, id]);

  const create = useCreateWebAccessControlRule(domainId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'pbr_web_access_control_rule', 'create');
      show({ tone: 'success', title: 'Web access control rule created' });
      navigate(listHref);
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateWebAccessControlRule(domainId ?? '', ruleId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'pbr_web_access_control_rule', 'edit');
      show({ tone: 'success', title: 'Web access control rule updated' });
      navigate(listHref);
    },
    onError: (e) => show({ tone: 'danger', title: 'Update failed', description: errMessage(e) }),
  });
  const saving = create.isPending || update.isPending;

  const nameValid = form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX;
  const canSubmit =
    nameValid &&
    !!form.path_pattern.trim() &&
    form.key_value.length > 0 && // vendor rejects an empty KeyValue (KeyValue.Invalid)
    form.websites.length > 0 &&
    !!domainId &&
    !saving;

  const submit = () => {
    const payload = {
      ...form,
      name: form.name.trim(),
      description: form.description.trim(),
      path_pattern: form.path_pattern.trim(),
    };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  };

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'Programmable Mitigation', slug: 'programmable-mitigation' },
              { label: 'Web Access Control Rule' },
            ]}
          />
        }
        title="Web Access Control Rule"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
        {/* General */}
        <Card flat>
          <CardHeader>
            <CardTitle>General</CardTitle>
          </CardHeader>
          <CardBody>
            <div className={styles.fieldGrid}>
              <Field
                label="Name"
                required
                description={`1-${NAME_MAX} characters. Valid: 0-9, A-Z, a-z, space, . _ - ( ) * +`}
              >
                {(props) => (
                  <Input
                    {...props}
                    value={form.name}
                    maxLength={NAME_MAX}
                    placeholder="Rule name"
                    onChange={(e) => set('name', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Description" description={`Up to ${DESC_MAX} characters.`}>
                {(props) => (
                  <div>
                    <Textarea
                      {...props}
                      value={form.description}
                      maxLength={DESC_MAX}
                      placeholder="Description"
                      onChange={(e) => set('description', e.target.value)}
                    />
                    <div className={styles.charCount}>
                      {form.description.length}/{DESC_MAX}
                    </div>
                  </div>
                )}
              </Field>
            </div>
          </CardBody>
        </Card>

        {/* Triggering Condition */}
        <Card flat>
          <CardHeader>
            <CardTitle>Triggering Condition</CardTitle>
            <CardDescription>The request must match all conditions below.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.fieldGrid}>
              <Field label="Path" required description="Configure the path targeted by this rule.">
                {() => (
                  <div className={styles.row2}>
                    <Select
                      value={form.path_operator}
                      onValueChange={(v) => set('path_operator', v)}
                    >
                      <SelectTrigger className={styles.select150}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PBR_PATH_CONDITIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      className={styles.flex1}
                      value={form.path_pattern}
                      maxLength={PATH_MAX}
                      placeholder="/api/cdn"
                      onChange={(e) => set('path_pattern', e.target.value)}
                    />
                  </div>
                )}
              </Field>

              <Field label="Key" required description="The request property to match against: IP address, browser Fingerprint, User-Agent string, or Cookie ID.">
                {() => (
                  <div className={styles.stack2}>
                    <Select value={form.key_type} onValueChange={(v) => set('key_type', v)}>
                      <SelectTrigger className={styles.selectMax300}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PBR_WEB_ACCESS_KEYS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={keyInput}
                      placeholder="Input your data and press Enter to submit"
                      onChange={(e) => setKeyInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addKeyValue();
                        }
                      }}
                    />
                    {form.key_value.length === 0 && (
                      <span style={{ color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-sm)' }}>
                        Add at least one value (type, then press Enter).
                      </span>
                    )}
                    <label className={styles.checkLabel}>
                      <Checkbox
                        checked={form.key_regex}
                        onCheckedChange={(c) => set('key_regex', c === true)}
                      />
                      <span>Treat key values as regular expressions</span>
                    </label>
                    {form.key_value.length > 0 && (
                      <div className={styles.tagWrap}>
                        {form.key_value.map((v) => (
                          <span key={v} className={styles.tag}>
                            {v}
                            <IconButton aria-label={`Remove ${v}`} variant="ghost" size="sm" onClick={() => removeKeyValue(v)}>
                              <Trash2 size={12} />
                            </IconButton>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Field>
            </div>
          </CardBody>
        </Card>

        {/* Device Type */}
        <Card flat>
          <CardHeader>
            <CardTitle>Device Type</CardTitle>
            <CardDescription>The rule applies to requests from the selected device types.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.deviceRow}>
              {PBR_DEVICE_TYPES.map((d) => (
                <label key={d.value} className={styles.checkLabel}>
                  <Checkbox checked={devSet.has(d.value)} onCheckedChange={() => toggleDev(d.value)} />
                  <span>{d.label}</span>
                </label>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Dynamic Action */}
        <Card flat>
          <CardHeader>
            <CardTitle>Dynamic Action</CardTitle>
            <CardDescription>An advanced protection strategy designed to prevent attackers from predicting the security policy by repetitive attacks.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.fieldGrid}>
              <Field label="Triggering Probability" required description="Configure the triggering probability of the action.">
                {() => (
                  <Select
                    value={String(form.triggering_probability)}
                    onValueChange={(v) => set('triggering_probability', Number(v))}
                  >
                    <SelectTrigger className={styles.selectMax160}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PBR_PROBABILITIES.map((p) => (
                        <SelectItem key={p} value={String(p)}>
                          {p}%
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
          </CardBody>
        </Card>

        {/* Auto-Web ACL */}
        <Card flat>
          <CardHeader>
            <CardTitle>Auto-Web ACL</CardTitle>
            <CardDescription>Auto-web ACL automatically controls passing or rejecting requests. Configure the key type and how long values live in the auto-web ACL.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.autoRow}>
              <Field label="Key" description="The request property (IP or Fingerprint) used to identify entries in the auto-web ACL list.">
                {() => (
                  <Select value={form.auto_list_key} onValueChange={(v) => set('auto_list_key', v)}>
                    <SelectTrigger className={styles.select160}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PBR_AUTO_LIST_KEYS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Duration (seconds)" description="How long the entry stays in the auto-web ACL before expiring.">
                {(props) => (
                  <Input
                    {...props}
                    type="number"
                    min={0}
                    value={form.auto_list_duration}
                    className={styles.input200}
                    onChange={(e) => set('auto_list_duration', Number(e.target.value))}
                  />
                )}
              </Field>
            </div>
          </CardBody>
        </Card>

        {/* Apply to */}
        <Card flat>
          <CardHeader>
            <CardTitle>Apply to</CardTitle>
            <CardDescription>Select the websites this rule applies to.</CardDescription>
          </CardHeader>
          <CardBody>
            {sites.length === 0 ? (
              <EmptyState icon={<Globe size={20} />} title="No websites available" description="Enable WAAP protection on a website first, then apply this rule." />
            ) : (
              <div className={styles.applyGrid}>
                {sites.map((s) => (
                  <label key={s.id} className={styles.checkLabel}>
                    <Checkbox checked={siteSet.has(s.id)} onCheckedChange={() => toggleSite(s.id)} />
                    <span>{s.domain}</span>
                  </label>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={() => navigate(listHref)}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Container>
  );
}
